import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './UserMenu';
import { HolderSwitcherChip } from './HolderSwitcher';
import { CreditBalanceIndicator, GuestTrialBanner } from '@/credits/components';
import { PrivilegedMfaBanner } from '@/auth/components/PrivilegedMfaBanner';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { NotificationBadge } from './ui/notification-badge';
import { useAuth } from '@/auth/hooks/useAuth';
import { FiasAIToggle } from '@/components/fias-ai/FiasAIToggle';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';
import { useNotificationSummary } from '@/hooks/useNotificationSummary';
import { WORKTOP_PATH } from '@/lib/arche-url';
import { useGamesPageAvailable } from '@/lib/games-availability';
import { useStorePageAvailable } from '@/lib/store-availability';

/**
 * The public Games page (ADR-035) — in the menu for everyone, signed in or not,
 * wherever its switch is on (`useGamesPageAvailable`).
 */
export const GAMES_PATH = '/a/games';

/**
 * The Arche Store: the new public store (`/a/store`) wherever its switch is on
 * — for everyone, signed in or not — and otherwise the old one (`/marketplace`,
 * signed in only), which stays as the Advanced search.
 */
export const STORE_PATH = '/a/store';
export const OLD_STORE_PATH = '/marketplace';

interface HeaderProps {
  /**
   * When true, shows only logo + theme toggle.
   * Hides navigation links, user menu, and hamburger menu on mobile.
   * Used for public arches when user is not logged in.
   */
  minimal?: boolean;
}

/**
 * Global Fias AI header toggle. Renders nothing when the provider is
 * absent (minimal header shells) or the user is signed out — the
 * provider itself is ALWAYS mounted now (it must be, or the window
 * crashes on public pages), so authentication is gated here at the
 * button, not by provider absence.
 */
function GlobalFiasAIToggle({ className }: { className?: string }) {
  const ai = useGlobalFiasAIOptional();
  const { isAuthenticated } = useAuth();
  if (!ai || !isAuthenticated) return null;
  return (
    <div className={`relative ${className ?? ''}`}>
      <FiasAIToggle onClick={ai.togglePanel} isActive={ai.isPanelOpen || ai.isMinimized} />
      {/* Scheduled-task completion alert — cascades into the window (schedules
          toggle → History tab). Renders nothing when the count is 0. */}
      <NotificationBadge
        count={ai.scheduledAlertCount}
        className="absolute -top-1.5 -right-1.5 ring-2 ring-background"
        aria-label={`${ai.scheduledAlertCount} scheduled task${
          ai.scheduledAlertCount === 1 ? '' : 's'
        } finished`}
      />
    </div>
  );
}

export function Header({ minimal = false }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const showGames = useGamesPageAvailable() === true;
  const newStore = useStorePageAvailable() === true;
  // Where "Arche Store" goes, or null when there is nowhere to send this visitor.
  const storePath = newStore ? STORE_PATH : isAuthenticated ? OLD_STORE_PATH : null;
  const { total: notificationTotal, totalMentions } = useNotificationSummary();

  // Minimal header: only logo and theme toggle
  if (minimal) {
    return (
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/">
                <img src="/fias-logo.png" alt="Fias" className="h-[42px] dark:invert" />
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {showGames && (
                <Link to={GAMES_PATH} className="text-primary hover:underline">
                  Games
                </Link>
              )}
              {newStore && (
                <Link to={STORE_PATH} className="text-primary hover:underline">
                  Arche Store
                </Link>
              )}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="border-b border-border bg-card">
      <GuestTrialBanner />
      <PrivilegedMfaBanner />
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img src="/fias-logo.png" alt="Fias" className="h-[42px] dark:invert" />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center space-x-4">
            {isAuthenticated && (
              <>
                <GlobalFiasAIToggle />
                <span className="relative inline-flex">
                  <Link to={WORKTOP_PATH} className="text-primary hover:underline">
                    Worktop
                  </Link>
                  <NotificationBadge
                    count={notificationTotal}
                    showMention={totalMentions > 0}
                    className="absolute -top-2 -right-3"
                  />
                </span>
                <Link to="/a/arche_make_a_request" className="text-primary hover:underline">
                  Re-Quest
                </Link>
              </>
            )}
            {showGames && (
              <Link to={GAMES_PATH} className="text-primary hover:underline">
                Games
              </Link>
            )}
            {storePath && (
              <Link to={storePath} className="text-primary hover:underline">
                Arche Store
              </Link>
            )}
            {/* Signed in, the theme switch lives in the account menu (UserMenu);
                signed out there is no menu to put it in, so it stays here. */}
            {!isAuthenticated && <ThemeToggle />}
            {isAuthenticated && <HolderSwitcherChip />}
            {isAuthenticated && <CreditBalanceIndicator />}
            <UserMenu />
          </nav>

          {/* Mobile navigation */}
          <div className="flex items-center space-x-2 lg:hidden">
            {/* See the desktop note above — signed in, it's in the menu Sheet. */}
            {!isAuthenticated && <ThemeToggle />}
            <GlobalFiasAIToggle />
            {isAuthenticated && (
              <span className="relative inline-flex">
                <Link
                  to={WORKTOP_PATH}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  Worktop
                </Link>
                <NotificationBadge
                  count={notificationTotal}
                  showMention={totalMentions > 0}
                  className="absolute -top-2 -right-3"
                />
              </span>
            )}
            {isAuthenticated && <CreditBalanceIndicator />}
            {isAuthenticated ? (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] flex flex-col gap-0">
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>
                  {/* Scrolls when the menu is taller than the viewport (e.g. a
                      short screen with the Account section expanded); the title
                      and close button stay fixed. min-h-0 lets the flex item
                      shrink below its content height so overflow can kick in. */}
                  <nav className="flex flex-col space-y-1 mt-6 flex-1 min-h-0 overflow-y-auto">
                    <div className="px-3 py-2">
                      <HolderSwitcherChip />
                    </div>
                    <div className="border-t border-border my-2" />
                    <Link
                      to="/a/arche_make_a_request"
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Re-Quest
                    </Link>
                    {showGames && (
                      <Link
                        to={GAMES_PATH}
                        className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Games
                      </Link>
                    )}
                    <Link
                      to={storePath ?? OLD_STORE_PATH}
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Arche Store
                    </Link>
                    <div className="border-t border-border my-2" />
                    <UserMenu showAccountLabel onItemSelect={() => setMobileMenuOpen(false)} />
                  </nav>
                </SheetContent>
              </Sheet>
            ) : (
              <>
                {showGames && (
                  <Link
                    to={GAMES_PATH}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Games
                  </Link>
                )}
                {newStore && (
                  <Link
                    to={STORE_PATH}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    Arche Store
                  </Link>
                )}
                <UserMenu />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
